import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@/components/Button/Button';
import { createClient } from '@/lib/supabase/component';
import { useAuth } from '@/components/Auth/AuthProvider';
import { toast } from 'sonner';
import { useCommentsStore, useAuthDialogStore } from '@/lib/stores/post';
import { cn } from '@/lib/utils/helpers';

/**
 * Comment form component for submitting new comments
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes to apply
 * @param {number} props.storyId - ID of the story/post to comment on
 * @param {string} props.replyToId - Optional ID of the comment being replied to
 * @returns {JSX.Element} CommentForm component
 */
const supabase = createClient();

export default function CommentForm({ className, storyId, replyToId }) {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth() || {};

  const { addComment } = useCommentsStore();
  const { openLoginDialog, openSignupDialog } = useAuthDialogStore();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!comment.trim()) return;

    if (!user) {
      toast.error('You must be logged in to comment');
      return;
    }

    setIsSubmitting(true);

    try {
      // Use the comments store to add the comment
      const { success, error } = await addComment(supabase, {
        storyId,
        userId: user.id,
        content: comment.trim(),
        replyToId
      });

      if (!success) {
        throw new Error(error || 'Failed to submit comment');
      }

      setComment('');
      toast.success('Comment submitted successfully!');
    } catch (err) {
      console.error('Error submitting comment:', err);
      toast.error(err.message || 'Failed to submit comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn('mb-8', className)}>
      {!user ? (
        <p>
          We love hearing from you. <button onClick={openLoginDialog} className="underline font-bold">Log in</button> or <button onClick={openSignupDialog} className="underline font-bold">Join</button> to leave a comment.
        </p>
      ) : (
        <form onSubmit={handleSubmit}>
          <textarea
            className="block w-full p-4 border border-navy dark:border-silver rounded-2xl mb-4 min-h-[100px] focus:outline-none focus:ring-1 focus:ring-navy dark:focus:ring-silver bg-transparent"
            placeholder="Enter Comment"
            aria-label="Comment text"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={isSubmitting}
            required
          />
          <div className="flex justify-end">
            <Button
              variant="default"
              type="submit"
              disabled={isSubmitting}
              className="min-w-[166px]"
            >
              {isSubmitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

CommentForm.propTypes = {
  className: PropTypes.string,
  storyId: PropTypes.number.isRequired,
  replyToId: PropTypes.string,
};
